import React, {
  useState,
  useEffect,
  useRef,
  SetStateAction,
  useMemo,
  useCallback,
} from "react";

import "./AnnouncementFormComponent.css";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import { CircularProgress } from "@material-ui/core";
import { AnnouncementNotification } from "../../../Redux/Actions/NotificationActions/AnnouncementNotificationAction";
import { handleSnackbar } from "../../../helpers/SnackbarHelper/SnackbarHelper";
import { useAppThunkDispatch } from "../../../Redux/hooks";
import { AdminInterFace } from "../../../Components/FeedComponents/Feed/Feed";
import { AnnouncementType, Masjid } from "../../../../Types";
import { toast } from "react-hot-toast";
import { Backdrop, InputAdornment, Modal, Typography } from "@mui/material";
import AnnouncementPreviewComponent from "../AnnouncementPreviewComponent/AnnouncementPreviewComponent";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import OTPDialog from "../../../Pages/Shared/OTPDialog/OTPDialog";
import { adminFromLocalStg } from "../../../helpers/AdminFromLocalStorage/AdminFromLocalStorage";
import { uploadImage } from "../../../helpers/ImageUploader/imageUploader";
import MultiMasjidSelectDropdown from "../../Shared/MulitMasjidSelectDropdown/MultiMasjidSelectDropdown";
import moment from "moment";
import "moment-timezone";
import calendar from "../../../Photos/Newuiphotos/Icons/calender.svg";
import { MdCancel } from "react-icons/md";
import tz_lookup from "tz-lookup";
import {
  CREATE_ANNOUNCEMENT,
  VERIFY_AND_SEND_ANNOUNCEMENT,
} from "../../../graphql/Notifications/mutation";
import { useMutation } from "@apollo/client";
import CarouselImageUploader from "../../Shared/CarouselImageUploader/CarouselImageUploader";

export type announceInputType = {
  _id?: string;
  masjidIds: string[];
  title: string;
  body: string;
  isSticky: boolean;
  image?: File[] | string | null;
  type: number;
  createdAt?: string;
  expiresAt: string | null;
  selectedMasjids: Masjid[];
};

export const ANNOUNCEMENT_TYPE_GENERAL = 0;
export const ANNOUNCEMENT_TYPE_HADITH = 999;

type PropsType = {
  setViewingAnnouncement: (value: boolean) => void;
  existingAnnouncement?: AnnouncementType | null;
  refetchAnnoucements?: () => void;
  type?: number;
  label?: string;
};

const getDefaultAnnouncementInfo = (type: number) => ({
  _id: undefined,
  masjidIds: [],
  title: "",
  body: "",
  isSticky: false,
  image: null,
  expiresAt: null,
  type,
  selectedMasjids: [],
});

const AnnouncementFormComponent = ({
  setViewingAnnouncement,
  existingAnnouncement,
  refetchAnnoucements,
  type = ANNOUNCEMENT_TYPE_GENERAL,
  label = "Announcement",
}: PropsType) => {
  const isHadith = type === ANNOUNCEMENT_TYPE_HADITH;
  const [openOTPModal, setOpenOTPModal] = useState<boolean>(false);
  const [admin, setAdmin] = useState<AdminInterFace>(adminFromLocalStg());
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [SelectedAll, setSelectedAll] = useState(isHadith);

  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [announcementData, setAnnouncementData] = useState<announceInputType>(
    getDefaultAnnouncementInfo(type)
  );
  const dispatch = useAppThunkDispatch();

  const [createAnnouncement] = useMutation(CREATE_ANNOUNCEMENT);
  const [verifyAndSendAnnouncement] = useMutation(VERIFY_AND_SEND_ANNOUNCEMENT);

  const clearForm = () => {
    setAnnouncementData(getDefaultAnnouncementInfo(type));
    setSelectedAll(false);
  };

  const [isLoadingStyles, setIsLoadingStyles] = useState(false);
  const handleOtpApiRequest = async (otp: string) => {
    setIsVerifying(true);
    try {
      const { data } = await verifyAndSendAnnouncement({
        variables: {
          otp: String(otp),
        },
      });

      if (data?.verifyAndSendAnnouncement) {
        toast.success(`${label} verified and sent successfully`);
        clearForm();
      } else {
        toast.error("Failed to verify OTP");
        clearForm();
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error("Failed to verify OTP");
      clearForm();
    } finally {
      setIsVerifying(false);
      setOpenOTPModal(false);
    }
  };

  const handleChange = (masjid: Masjid[] | null) => {
    setAnnouncementData({
      ...announcementData,
      selectedMasjids: masjid ?? [],
    });
  };
  const handleMasjidToggle = () => {
    if (SelectedAll) {
      setSelectedAll(false);
    } else {
      setSelectedAll(true);
      setAnnouncementData((prevState) => ({
        ...prevState,
        selectedMasjids: [],
      }));
    }
  };

  const handleAllMasjidsSubmission = async (imageUrl: string) => {
    const loadingToastId = toast.loading(
      existingAnnouncement?._id
        ? `Updating ${label}...`
        : "Sending notification..."
    );

    try {
      const { data } = await createAnnouncement({
        variables: {
          input: {
            title: announcementData.title,
            body: announcementData.body,
            type,
            isSticky: false,
            image: imageUrl,
            masjidIds: [],
            expiresAt: "",
          },
        },
      });

      if (data?.createAnnouncement) {
        toast.dismiss(loadingToastId);
        if (!existingAnnouncement) {
          setShowConfirmation(false);
          setOpenOTPModal(true);
        } else {
          setViewingAnnouncement(true);
          refetchAnnoucements?.();
        }
      } else {
        throw new Error("Failed to create announcement");
      }
    } catch (error) {
      console.error("Error creating announcement:", error);
      toast.dismiss(loadingToastId);
      setIsTriggering(false);
      handleSnackbar(
        true,
        "warning",
        "Failed to Send the Notification to the Application",
        dispatch
      );
      clearForm();
    }
    setShowConfirmation(false);
  };

  const handleIndividualMasjidsSubmission = async (imageUrl: string) => {
    const selectedMasjids = announcementData.selectedMasjids;
    const loadingToastId = toast.loading(`Sending OTP...`);

    try {
      for (const masjid of selectedMasjids) {
        const [longitude, latitude] = masjid.location.coordinates;
        const timezone = tz_lookup(latitude, longitude);

        const { data } = await createAnnouncement({
          variables: {
            input: {
              title: announcementData.title,
              body: announcementData.body,
              type,
              isSticky: false,
              image: imageUrl,
              masjidIds: [masjid._id],
              expiresAt: "",
            },
          },
        });

        if (!data?.createAnnouncement) {
          throw new Error(
            `Failed to send notification to ${masjid.masjidName}`
          );
        }
      }

      toast.success("OTP Sent Successfully", { id: loadingToastId });
      setShowConfirmation(false);
      setOpenOTPModal(true);
    } catch (error) {
      console.error("Error creating announcements:", error);
      toast.dismiss(loadingToastId);
      setIsTriggering(false);
      handleSnackbar(
        true,
        "warning",
        "Failed to Send Notifications to Some Masjids",
        dispatch
      );
      clearForm();
    }
    setShowConfirmation(false);
  };

  const handleAnnoucementNetworkCall = async () => {
    const uploadIfNeeded = async () => {
      if (announcementData?.image && announcementData.image instanceof File) {
        toast.loading("Uploading image...");
        try {
          const uploadedUrl = await uploadImage(announcementData.image);
          return uploadedUrl;
        } catch (error) {
          toast.dismiss();
          toast.error("Image upload failed");
          throw error;
        }
      }
      return announcementData?.image || "";
    };

    const imageUrl = await uploadIfNeeded();
    toast.dismiss();

    if (SelectedAll) {
      await handleAllMasjidsSubmission(imageUrl);
    } else {
      await handleIndividualMasjidsSubmission(imageUrl);
    }
  };

  const handlesubmit = () => {
    setIsTriggering(true);
    if (!SelectedAll) {
      if (
        announcementData.title &&
        announcementData.body &&
        (existingAnnouncement ||
          (announcementData?.selectedMasjids &&
            announcementData?.selectedMasjids?.length > 0))
      ) {
        // handleAnnoucementNetworkCall();
        setShowPreview(true);
      } else {
        setIsTriggering(false);
        handleSnackbar(
          true,
          "warning",
          "Please Enter all Details Properly",
          dispatch
        );
      }
    } else if (SelectedAll) {
      if (announcementData.title && announcementData.body && SelectedAll) {
        setShowPreview(true);
      } else {
        setIsTriggering(false);
        handleSnackbar(
          true,
          "warning",
          "Please Enter all Details Properly",
          dispatch
        );
      }
    } else {
      setIsTriggering(false);

      handleSnackbar(
        true,
        "warning",
        "Couldnt Send the Notification : Something Went Wrong ",
        dispatch
      );
    }
  };

  const handleCancelOrSubmit = (isSubmit = false) => {
    setShowPreview(false);
    setIsTriggering(false);
    if (isSubmit) setShowConfirmation(true);
  };
  const handleImageUpload = (e: any) => {
    const newImages = [];
    newImages.push(e.target.files[0]);
    setAnnouncementData((prevState) => ({
      ...prevState,
      image: e.target.files[0],
    }));
  };
  const handleDeleteImage = () => {
    setAnnouncementData((prevState) => ({
      ...prevState,
      image: null,
    }));
  };
  const handleDateSelect = useCallback((date: Date) => {
    // Use moment to parse the date in the specific timezone
    console.log(date);
    const dateInTz = moment(date);

    // Convert it to UTC before storing in the database
    const formattedDate = dateInTz.utc().format();

    setAnnouncementData((prev) => ({
      ...prev,
      expiresAt: formattedDate,
    }));

    setIsCalendarOpen(false);

    // Clear validation error for expiresAt if it exists
    // if (validationErrors.expiresAt) {
    //   setValidationErrors((prev) => ({
    //     ...prev,
    //     expiresAt: false,
    //   }));
    // }
  }, []);

  const handleOpenCalendar = useCallback(() => {
    setIsCalendarOpen(true);
  }, []);
  const getExpiresAtDate = useCallback(() => {
    if (!announcementData.expiresAt) return undefined;
    return moment(announcementData.expiresAt).toDate();
  }, [announcementData.expiresAt]);
  const uploaderImages = useMemo(() => {
    if (!announcementData?.image) return [];
    if (announcementData.image instanceof File) {
      return [
        {
          url: URL.createObjectURL(announcementData?.image),
          alt: "Image 0",
          createdByObjectURL: true, // Mark this URL as created by URL.createObjectURL
        },
      ];
    } else {
      return [
        {
          url: announcementData?.image,
          alt: "Image 0",
          createdByObjectURL: false, // Mark this URL as created by URL.createObjectURL
        },
      ];
    }
  }, [announcementData?.image]);

  const titleCharLimit = isHadith ? undefined : 30;
  const bodyCharLimit = isHadith ? undefined : 200;

  const handleFieldChange = (field: "title" | "body", value: string, charLimit?: number) => {
    if (!charLimit || value.length <= charLimit) {
      setAnnouncementData((prev) => ({ ...prev, [field]: value }));
    }
  };

  return (
    <>
      <div className="AnnouncementContainer">
        <div className="AnnouncementContainerBody">
          <Box sx={{ position: "relative" }}>
            <Typography
              variant="h6"
              style={{
                textAlign: "center",
                marginBottom: "1rem",
                marginTop: "1rem",
              }}
            >
              {existingAnnouncement
                ? `Update ${label}`
                : `Make ${label}`}
            </Typography>
            {existingAnnouncement && (
              <MdCancel
                size={25}
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "10px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setViewingAnnouncement(true);
                }}
              />
            )}
          </Box>
          {isLoadingStyles ? (
            <CircularProgress />
          ) : (
            <CarouselImageUploader
              images={uploaderImages}
              onUpload={handleImageUpload}
              onDelete={handleDeleteImage}
              isCarousel={false}
            ></CarouselImageUploader>
          )}

          <TextField
            value={announcementData.title}
            autoFocus
            margin="dense"
            id="name"
            autoComplete="Off"
            error={isTriggering && !announcementData.title}
            helperText={titleCharLimit ? `${announcementData.title.length} / ${titleCharLimit}` : undefined}
            label={`${label} Title`}
            onChange={(e) => handleFieldChange("title", e.target.value, titleCharLimit)}
            sx={{ marginBottom: 3 }}
            type="text"
            fullWidth
            variant="outlined"
            inputProps={titleCharLimit ? { maxLength: titleCharLimit } : {}}
          />
          <TextField
            value={announcementData.body}
            id="outlined-required"
            error={isTriggering && !announcementData.body}
            helperText={bodyCharLimit ? `${announcementData.body.length} / ${bodyCharLimit}` : undefined}
            variant="outlined"
            multiline
            rows={5}
            autoComplete="Off"
            onChange={(e) => handleFieldChange("body", e.target.value, bodyCharLimit)}
            label={`${label} Body`}
            fullWidth
            type="email"
            sx={{ marginBottom: 3 }}
            inputProps={bodyCharLimit ? { maxLength: bodyCharLimit } : {}}
          />
          {/* <CustomRadio
            options={[
              { value: "showNow", label: "Show Now" },
              { value: "sticky", label: "Sticky" },
            ]}
            value={announcementData.isSticky ? "sticky" : "showNow"}
            onChange={(value) => {
              setAnnouncementData((prev) => ({
                ...prev,
                isSticky: value === "sticky",
                expiresAt:
                  value === "showNow"
                    ? moment().endOf("day").toISOString()
                    : announcementData.expiresAt,
              }));
            }}
          /> */}
          {/* {announcementData.isSticky && ( */}
          <Box sx={{ mb: "1.5rem", mt: "1.5rem" }}>
            {/* <TextField
              fullWidth
              label="Expire On"
              name="expiresAt"
              value={
                announcementData?.expiresAt
                  ? moment(announcementData.expiresAt).format("DD MMM YYYY")
                  : ""
              }
              onClick={handleOpenCalendar}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <img
                      src={calendar}
                      alt="calendar"
                      onClick={handleOpenCalendar}
                      style={{
                        cursor: "pointer",
                        color: "#545454",
                        marginRight: "10px",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                  },
                  cursor: "pointer",
                  "& fieldset": {
                    borderColor: "#545454",
                  },
                  "&:hover fieldset": {
                    borderColor: "#545454",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#545454",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#545454",
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                  },
                },
              }}
            /> */}
          </Box>
          {/* )} */}
          {!existingAnnouncement && !isHadith && (
            <>
              <span className="itemKey">
                Choose All Masjids
                <Switch
                  checked={SelectedAll}
                  onChange={handleMasjidToggle}
                  name="loading"
                  color="primary"
                />
              </span>
              {!SelectedAll && (
                <MultiMasjidSelectDropdown
                  selectedMasjids={announcementData?.selectedMasjids ?? []}
                  handleChange={handleChange}
                  isMultiple={true}
                />
              )}
              {!announcementData.selectedMasjids.length && !SelectedAll ? (
                <span style={{ color: "red" }}>
                  Choose Masjids Please Before triggering
                </span>
              ) : (
                <> </>
              )}
            </>
          )}

          <button className="ButtonToggle" onClick={handlesubmit}>
            <span className="text">
              {isTriggering ? (
                <CircularProgress size="20px" />
              ) : existingAnnouncement ? (
                `Update ${label}`
              ) : (
                `Trigger ${label}`
              )}
            </span>
          </button>
          {!existingAnnouncement && (
            <div className="AnnouncementTtitleContainerForm">
              <button
                className="ButtonToggleViewAll"
                onClick={() => {
                  setViewingAnnouncement(true);
                }}
              >
                {`View All ${label}s`}
              </button>
            </div>
          )}
        </div>
      </div>
      <Modal open={showPreview} onClose={() => setShowPreview(false)}>
        <AnnouncementPreviewComponent
          announcementData={announcementData}
          onCancel={() => handleCancelOrSubmit(false)}
          onSubmit={() => handleCancelOrSubmit(true)}
          label={label}
        />
      </Modal>
      <ConfirmationModal
        open={showConfirmation}
        onYes={() => handleAnnoucementNetworkCall()}
        onNo={() => setShowConfirmation(false)}
      />

      <OTPDialog
        open={openOTPModal}
        email={admin.email}
        setOpen={setOpenOTPModal}
        handleOtpApiRequest={handleOtpApiRequest}
        isVerifying={isVerifying}
      />
      {/* {isCalendarOpen && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isCalendarOpen}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsCalendarOpen(false);
            }
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: "1rem",
              width: "100%",
              maxWidth: "400px",
            }}
          >
            <CustomCalender
              onDateSelect={handleDateSelect}
              value={getExpiresAtDate()}
              setValue={(date: Date) => handleDateSelect(date)}
              tileDisabled={() => false}
            />
          </Box>
        </Backdrop>
      )} */}
    </>
  );
};

export default AnnouncementFormComponent;
